/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import React from 'react';
import { arrayOf, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import NoImageIcon from './NoImageIcon';
import css from './ResponsiveImage.module.css';

function getUrlVariants(secureUrl, variant) {

  // Define the variants
  // q_auto,f_auto: let Cloudinary pick the best compression level and format (WebP/AVIF
  // when the browser supports it) automatically. Same visual quality, much smaller files.
  const variants = {
    'listing-card-6x': { transformation: 'w_2400,h_2400,q_auto,f_auto', height: 2400, width: 2400 },
    'listing-card-4x': { transformation: 'w_1600,h_1600,q_auto,f_auto', height: 1600, width: 1600 },
    'listing-card-2x': { transformation: 'w_800,h_800,q_auto,f_auto', height: 800, width: 800 },
    'listing-card': { transformation: 'w_400,h_400,q_auto,f_auto', height: 400, width: 400 },
    // Search result cards: 250x250 on mobile (1x) / 500x500 on desktop (1x) / 1000x1000 for 2x
    // (retina desktop & 2x mobile). These are emitted together as a responsive srcSet so the
    // browser downloads only the size the card actually needs at the current viewport + DPR.
    'search-card-mobile': { transformation: 'w_250,h_250,q_auto,f_auto', height: 250, width: 250 },
    'search-card': { transformation: 'w_500,h_500,q_auto,f_auto', height: 500, width: 500 },
    'search-card-2x': { transformation: 'w_1000,h_1000,q_auto,f_auto', height: 1000, width: 1000 },
    // 'listing-card-6x': { transformation: 'c_fill,g_auto,w_2400,h_2400', height: 2400, width: 2400 },
    // 'listing-card-4x': { transformation: 'c_fill,g_auto,w_1600,h_1600', height: 1600, width: 1600 },
    // 'listing-card-2x': { transformation: 'c_fill,g_auto,w_800,h_800', height: 800, width: 800 },
    // 'listing-card': { transformation: 'c_fill,g_auto,w_400,h_400', height: 400, width: 400 },
    // c_limit + width only: preserves the original aspect ratio (no forced crop, no distortion),
    // just caps the download size. Use this instead of a fixed-aspect-ratio crop variant when
    // CSS (e.g. object-fit: cover) is already responsible for the visual crop - otherwise the
    // Cloudinary crop and the CSS crop compound and can cut out the actual subject of the photo.
    'width-limit-800': { transformation: 'c_limit,w_800,q_auto,f_auto', width: 800 },
    'landscape-crop': { transformation: 'c_crop,w_400,h_267,q_auto,f_auto', height: 267, width: 400 },
    'landscape-crop2x': { transformation: 'c_crop,w_800,h_533,q_auto,f_auto', height: 533, width: 800 },
    'landscape-crop4x': { transformation: 'c_crop,w_1600,h_1066,q_auto,f_auto', height: 1066, width: 1600 },
    'landscape-crop6x': { transformation: 'c_crop,w_2400,h_1602,q_auto,f_auto', height: 1602, width: 2400 },
    'scaled-small': { transformation: 'w_320,h_320,q_auto,f_auto', height: 320, width: 320 },
    'scaled-medium': { transformation: 'w_750,h_750,q_auto,f_auto', height: 750, width: 750 },
    'scaled-large': { transformation: 'w_1024,h_1024,q_auto,f_auto', height: 1024, width: 1024 },
    'scaled-xlarge': { transformation: 'w_2400,h_2400,q_auto,f_auto', height: 2400, width: 2400 },
    'square-small': { transformation: 'c_crop,w_240,h_240,q_auto,f_auto', height: 240, width: 240 },
    'square-small2x': { transformation: 'c_crop,w_480,h_480,q_auto,f_auto', height: 480, width: 480 },
    'facebook': { transformation: 'c_crop,w_1200,h_630,q_auto,f_auto', height: 630, width: 1200 },
    'twitter': { transformation: 'c_crop,w_600,h_314,q_auto,f_auto', height: 314, width: 600 },
    'default': { transformation: 'q_auto,f_auto' }
  };

  const splittedURL = secureUrl.includes('/upload') ? secureUrl.split('/upload') : null;
  // If the URL contains '/upload', process the transformation as before.
  // If the requested variant name isn't in the map above (e.g. a typo, or a name that only
  // exists as a Sharetribe SDK image variant elsewhere), fall back to a bounded default
  // transformation instead of silently returning the untransformed (often much larger) original.
  if (splittedURL && splittedURL.length === 2) {
    // Fall back to a width cap (no forced crop) rather than a fixed-aspect-ratio crop variant:
    // the caller's CSS (e.g. object-fit: cover) usually already handles the visual crop, and an
    // unrelated forced crop here can cut out the actual subject of photos that aren't that shape.
    const { transformation } = variants[variant] || variants['width-limit-800'];
    return {
      variants,
      imageUrl: `${splittedURL[0]}/upload/${transformation}${splittedURL[1]}`,
    };
  }

  // Handle blob URLs (or any URLs without '/upload')
  if (secureUrl.startsWith('blob:')) {
    return {
      variants,
      imageUrl: secureUrl, // Return the blob URL as is
    };
  }
  if (secureUrl.startsWith('https:')) {
    return {
      variants,
      imageUrl: secureUrl, // Return the blob URL as is
    };
  }
  // Return variants only if no valid URL was processed
  return { variants };
}

// Build a responsive srcSet for a raw (Cloudinary) image URL string across the given variant
// names. Mirrors the SDK-image branch: each variant becomes a `<url> <width>w` candidate so the
// browser can pick the smallest source that satisfies the card's `sizes` at the current DPR.
// The first variant is used as the default `src` (fallback for browsers without srcSet support).
function getStringUrlSources(sourceUrl, variantNames) {
  const { variants, imageUrl: defaultUrl } = getUrlVariants(sourceUrl, variantNames[0]);

  const candidates = variantNames
    .map(name => {
      const width = variants[name]?.width;
      const { imageUrl } = getUrlVariants(sourceUrl, name);
      return imageUrl && width ? `${imageUrl} ${width}w` : null;
    })
    .filter(Boolean);

  // Intrinsic dimensions of the variant used as `src`. Emitting width/height lets the browser
  // reserve the right box before the image arrives, which is what keeps this image from
  // contributing to Cumulative Layout Shift. (CSS such as width:100%/object-fit still wins for
  // the rendered size -- these attributes only communicate the aspect ratio.)
  const defaultVariant = variants[variantNames[0]];

  return {
    src: defaultUrl,
    // Only emit srcSet when we have at least two width-tagged candidates; a single-entry
    // srcSet adds nothing over `src`.
    srcSet: candidates.length > 1 ? candidates.join(', ') : undefined,
    width: defaultVariant?.width,
    height: defaultVariant?.height,
  };
}

const ResponsiveImage = props => {
  const {
    className,
    rootClassName,
    alt,
    noImageMessage,
    image,
    url,
    file,
    variants,
    dimensions,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  if (url && typeof url == 'string' && variants && variants.length) {
    const { src, srcSet, width, height } = getStringUrlSources(url, variants);
    const imgProps = {
      className: classes,
      src,
      ...(srcSet ? { srcSet } : {}),
      // Only useful as a pair: a lone width tells the browser nothing about the aspect
      // ratio, which is the whole point of declaring these.
      ...(width && height ? { width, height } : {}),
      ...rest,
    };

    return <img alt={alt} {...imgProps} />;
  } else if (file && typeof file == 'string' && variants && variants.length) {
    const { src, srcSet, width, height } = getStringUrlSources(file, variants);
    const imgProps = {
      className: classes,
      src,
      ...(srcSet ? { srcSet } : {}),
      // Only useful as a pair: a lone width tells the browser nothing about the aspect
      // ratio, which is the whole point of declaring these.
      ...(width && height ? { width, height } : {}),
      ...rest,
    };

    return <img alt={alt} {...imgProps} />;
  } else if (url && typeof url == 'string') {
    // No variants were requested, so there is no known intrinsic size to declare here.
    // `classes` is still applied so this branch is styled like every other one.
    return <img alt={alt} className={classes} src={url} {...rest} />;
  } else if (image == null || variants.length === 0) {
    const noImageClasses = classNames(rootClassName || css.root, css.noImageContainer, className);

    const noImageMessageText = noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;
    return (
      <div className={noImageClasses}>
        <div className={css.noImageWrapper}>
          <NoImageIcon className={css.noImageIcon} />
          <div className={css.noImageText}>{noImageMessageText}</div>
        </div>
      </div>
    );
  }

  const imageVariants = image.attributes.variants;
  const variantUrls = variants
    .map(variantName => imageVariants[variantName])
    .filter(Boolean);
  const fallbackVariant = variantUrls[0];

  const srcSet = variantUrls
    .map(variant => {
      return `${variant.url} ${variant.width}w`;
    })
    .join(', ');

  const imgProps = {
    className: classes,
    src: fallbackVariant?.url,
    srcSet,
    width: fallbackVariant?.width,
    height: fallbackVariant?.height,
    ...rest,
  };

  return <img alt={alt} {...imgProps} />;
};

ResponsiveImage.defaultProps = {
  className: null,
  rootClassName: null,
  image: null,
  sizes: null,
  noImageMessage: null,
};

ResponsiveImage.propTypes = {
  className: string,
  rootClassName: string,
  alt: string.isRequired,
  image: oneOfType([propTypes.image, propTypes.imageAsset]),
  variants: arrayOf(string),
  sizes: string,
  noImageMessage: string,
};

export default ResponsiveImage;
